export const serviceSlugs: { slug: string; title: string }[] = [
  { slug: 'social-media-marketing', title: 'Social Media Marketing' },
  { slug: 'paid-ads', title: 'Paid Ads (Meta Ads)' },
  { slug: 'seo', title: 'SEO' },
  { slug: 'website-development', title: 'Website Development' },
  { slug: 'lead-generation', title: 'Lead Generation' },
  { slug: 'funnel-building', title: 'Funnel Building' },
]

export interface ServicePageContent {
  slug: string
  title: string
  tagline: string
  intro: string
  sections: { heading: string; content: string; bullets?: string[] }[]
  outcomeTitle: string
  outcomeBullets: string[]
}

export const servicePagesContent: ServicePageContent[] = [
  {
    slug: 'social-media-marketing',
    title: 'Social Media Marketing',
    tagline: 'Build a Brand Presence That Earns Trust',
    intro:
      'In today\'s digital world, your audience often meets your brand on social media before they ever speak to you. A strong social media presence helps businesses build credibility, communicate expertise, and stay visible to the right audience. At DigiSutra, we approach social media with a clear objective — strengthening your brand presence so that potential customers trust your business before they even enquire.',
    sections: [
      {
        heading: 'What We Focus On',
        content: 'Strategic Content Planning — Every post should serve a purpose. We design content that aligns with your brand positioning, audience interests, and business goals.',
      },
      {
        heading: 'Consistent Brand Communication',
        content: 'Consistency builds recognition. We ensure your messaging remains clear, professional, and aligned with your brand identity.',
      },
      {
        heading: 'Audience Engagement',
        content: 'Social media is not just about broadcasting content. We help brands create conversations that build trust and long-term relationships.',
      },
    ],
    outcomeTitle: 'The Outcome',
    outcomeBullets: [
      'Stronger brand credibility',
      'Better audience engagement',
      'A digital presence that supports lead generation efforts',
    ],
  },
  {
    slug: 'paid-ads',
    title: 'Paid Ads (Meta Ads)',
    tagline: 'Reach the Right Audience at the Right Time',
    intro:
      'Advertising should not feel like gambling. With the right strategy and targeting, paid advertising becomes a predictable engine for lead generation and business growth. At DigiSutra, our focus is Meta Ads performance marketing designed specifically for generating qualified enquiries.',
    sections: [
      {
        heading: 'Audience Research',
        content: 'We identify the people most likely to benefit from your product or service. This ensures every campaign reaches high-intent prospects.',
      },
      {
        heading: 'Data-Driven Campaign Setup',
        content: 'From creatives to targeting and budget allocation, every decision is guided by data and performance insights.',
      },
      {
        heading: 'Continuous Optimization',
        content: 'Campaign performance is continuously monitored and improved to deliver better cost per lead and higher quality enquiries.',
      },
    ],
    outcomeTitle: 'The Outcome',
    outcomeBullets: [
      'Qualified business enquiries',
      'Measurable return on advertising spend',
      'Scalable lead generation systems',
    ],
  },
  {
    slug: 'seo',
    title: 'SEO',
    tagline: 'Build Long-Term Visibility on Search Engines',
    intro:
      'Search engines remain one of the most powerful sources of organic traffic and long-term business opportunities. SEO is not about shortcuts. It is about building structured visibility that helps your business appear when potential customers actively search for your services.',
    sections: [
      { heading: 'Keyword Strategy', content: 'We identify the search terms your potential customers are actually using.' },
      {
        heading: 'On-Page Optimization',
        content: 'We optimize your website structure, content, and technical elements so search engines clearly understand your business.',
      },
      {
        heading: 'Authority Building',
        content: 'Through consistent optimization and strategic content, we help your website grow its authority over time.',
      },
    ],
    outcomeTitle: 'The Outcome',
    outcomeBullets: [
      'Higher search engine visibility',
      'Consistent organic traffic',
      'Long-term digital credibility',
    ],
  },
  {
    slug: 'website-development',
    title: 'Website Development',
    tagline: 'Your Website Should Work Like Your Best Sales Representative',
    intro:
      'A website is often the first real interaction someone has with your brand. If the experience is confusing or slow, potential customers leave before understanding your value. At DigiSutra, we build websites that combine clarity, speed, and conversion-focused design.',
    sections: [
      {
        heading: 'Conversion-Focused Design',
        content: 'Every section of your website should guide visitors toward taking action.',
      },
      {
        heading: 'Fast and Reliable Performance',
        content: 'Website speed and stability directly impact user experience and search visibility.',
      },
      {
        heading: 'Mobile-First Experience',
        content: 'Most visitors access websites through mobile devices. We ensure your website performs perfectly across all screens.',
      },
    ],
    outcomeTitle: 'The Outcome',
    outcomeBullets: [
      'Strong first impression',
      'Clear brand communication',
      'Higher enquiry and conversion rates',
    ],
  },
  {
    slug: 'lead-generation',
    title: 'Lead Generation',
    tagline: 'Turn Attention Into Real Business Opportunities',
    intro:
      'Traffic and followers alone do not build businesses. What matters is generating qualified enquiries from people genuinely interested in your offering. At DigiSutra, we design structured lead generation systems that help businesses attract the right prospects consistently.',
    sections: [
      {
        heading: 'Audience Targeting',
        content: 'We focus on reaching individuals who are most likely to become customers.',
      },
      {
        heading: 'Campaign Structure',
        content: 'Lead generation campaigns are built with clear messaging and simple actions that encourage enquiries.',
      },
      {
        heading: 'Lead Quality Optimization',
        content: 'We continuously refine campaigns to improve lead quality and conversion potential.',
      },
    ],
    outcomeTitle: 'The Outcome',
    outcomeBullets: [
      'Consistent flow of enquiries',
      'Higher quality prospects',
      'Predictable lead pipelines',
    ],
  },
  {
    slug: 'funnel-building',
    title: 'Funnel Building',
    tagline: 'Convert Prospects Into Customers',
    intro:
      'Generating leads is only the beginning. Without a structured journey, many opportunities are lost before becoming customers. Funnels help guide prospects through a clear decision-making process.',
    sections: [
      {
        heading: 'Awareness Stage',
        content: 'Introduce your brand and communicate the value of your offering.',
      },
      {
        heading: 'Consideration Stage',
        content: 'Provide information and clarity that helps prospects evaluate your solution.',
      },
      {
        heading: 'Decision Stage',
        content: 'Create a clear path that encourages prospects to take action and become customers.',
      },
    ],
    outcomeTitle: 'The Outcome',
    outcomeBullets: [
      'Better lead nurturing',
      'Higher conversion rates',
      'Stronger customer relationships',
    ],
  },
]

/** FAQs per service slug — powers FAQPage schema (AEO) + visible FAQ section. */
export const serviceFAQs: Record<string, { question: string; answer: string }[]> = {
  'social-media-marketing': [
    { question: 'What is social media marketing for B2B businesses?', answer: 'Social media marketing for B2B businesses involves building brand credibility, communicating expertise, and staying visible to decision-makers through strategic content on platforms like Instagram, LinkedIn, and Facebook. DigiSutra focuses on B2B social media that supports lead generation.' },
    { question: 'How much does social media marketing cost in Surat?', answer: 'Social media marketing pricing depends on the platforms, posting frequency, and content type you need. DigiSutra offers customized packages based on your goals rather than fixed rates — book a strategy call for a tailored quote.' },
    { question: 'How long does it take to see results from social media marketing?', answer: 'Typically 3–6 months to see meaningful brand awareness growth and 6–12 months for consistent lead generation from organic social media. Meta Ads (paid) can generate leads within weeks.' },
  ],
  'paid-ads': [
    { question: 'What are Meta Ads and how do they work for lead generation?', answer: 'Meta Ads are paid advertising campaigns on Facebook and Instagram. They use detailed targeting to reach specific audiences — by location, interests, job title, and behavior. DigiSutra uses Meta Ads to generate qualified enquiries for B2B, real estate, and marble businesses.' },
    { question: 'How much should I spend on Meta Ads in Surat?', answer: 'The right Meta Ads budget depends on your industry, competition, and goals. DigiSutra helps you start with a sensible budget and scales it as we prove ROI — we recommend the right number during your strategy call.' },
    { question: 'What is the ROI of Meta Ads for real estate?', answer: 'Well-managed Meta Ads campaigns for real estate can generate a steady flow of qualified enquiries. DigiSutra focuses on lead quality over quantity, ensuring enquiries come from genuine buyers rather than just clicks.' },
  ],
  seo: [
    { question: 'How long does SEO take to show results?', answer: 'SEO typically shows measurable improvement in 3–6 months and significant results in 6–12 months. DigiSutra focuses on long-term organic visibility for B2B keywords in Surat and across India.' },
    { question: 'What is SEO and why does my business need it?', answer: 'SEO (Search Engine Optimization) improves your website\'s visibility on Google so potential customers find you when they search for services you offer. It\'s a long-term investment in organic traffic and credibility.' },
    { question: 'Which is the best SEO agency in Surat?', answer: 'DigiSutra specializes in SEO for B2B and high-value businesses in Surat and Gujarat. We focus on keyword strategy, on-page optimization, content creation, and link building for measurable ranking improvements.' },
  ],
  'website-development': [
    { question: 'How much does a business website cost in Surat?', answer: 'Website pricing depends on the number of pages, features, and complexity you need. DigiSutra builds conversion-focused websites scoped to your goals — book a call for a tailored quote.' },
    { question: 'What makes a good business website for lead generation?', answer: 'A lead-generating website needs fast loading speed, mobile-first design, clear call-to-actions, trust signals (testimonials, case studies), and proper SEO structure. DigiSutra builds all of these into every website.' },
  ],
  'lead-generation': [
    { question: 'What is lead generation and how does it work for B2B businesses?', answer: 'Lead generation is the process of attracting and converting prospects into people who are interested in your product or service. For B2B businesses, DigiSutra uses a combination of Meta Ads, SEO, landing pages, and follow-up systems to create a consistent pipeline of qualified enquiries.' },
    { question: 'How many leads can I expect per month?', answer: 'Lead volume depends on your budget, industry, and competition. DigiSutra focuses on consistent, qualified enquiries over vanity numbers, and we set realistic expectations based on your specific market during the strategy call.' },
    { question: 'What is the best lead generation agency in Surat?', answer: 'DigiSutra is a specialized lead generation agency in Surat focused on B2B, real estate, and marble businesses. We use the BLS Method — Brand, Lead, Sales — to build structured pipelines.' },
  ],
  'funnel-building': [
    { question: 'What is a marketing funnel and why does my business need one?', answer: 'A marketing funnel is the customer journey from awareness to purchase decision. Without a funnel, you lose prospects at every stage. DigiSutra builds funnels that capture interest, nurture leads, and convert them into paying clients.' },
    { question: 'How much does funnel building cost?', answer: 'Funnel building pricing varies with complexity — from a simple landing page and email sequence to multi-step automated funnels. DigiSutra scopes and quotes based on your specific needs.' },
  ],
}
